import React, { useCallback, useEffect, useState } from "react";
import { AutoComplete, Modal, NoteTooltip } from "MODULES/shared/components";
import { Body, Close, Footer, Header } from "MODULES/shared/components/Modal";

import { Form as AppForm } from "MODULES/shared/components/Forms";
import api from "APP_ROOT/api";
import { get, set } from "lodash";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import {
  DISABLED,
  ENABLED,
  INVENTORY_ACCOUNT,
  PURCHASE_ORDERS_QUICKBOOKS_TYPE,
  QUICKBOOKS_CUSTOMER_PURCHASE_FIELD,
  QUICKBOOKS_INVENTORY_TRACKING,
  REDUCE_COST_AT_SALE,
  REDUCE_REPAIR_COST_AT_SALE,
  SOLD_INVENTORY_ITEM
} from "APP_ROOT/constants";
import { PURCHASE_ORDER_QUICKBOOKS_TYPES } from "MODULES/PurchaseOrders/constants";
import { properCase } from "LIB/formatters/text";
import { Col } from "react-bootstrap";
import { toastr } from "react-redux-toastr";
import Check from "../../shared/components/Forms/Check";
import CheckGroup from "../../shared/components/Forms/Check/CheckGroup";

const NON_INVENTORY = "NonInventory";
const SERVICE = "Service";

function SettingsModal({
  closed,
  close,
  defaultQuickbooksAccount,
  locationId,
  editing,
  saveSetting,
  setDefaultQuickbooksAccount,
  settings = {},
  submitting = false,
  updateSetting
}) {
  if (closed) {
    return null;
  }

  const [prefs, setPrefs] = useState([]);

  const search = params =>
    api.locations.quickbooks.accounts
      .search(locationId, params)
      .then(({ data }) => data.results);

  const searchItems = types => filter =>
    api.locations.quickbooks.items
      .search(locationId, filter, types)
      .then(({ data }) => data.results);

  useEffect(
    () => {
      api.locations.quickbooks.preferences
        .search(locationId)
        .then(({ data }) => {
          setPrefs(data.results);
          return function cleanup() {};
        })
        .catch(response => {
          toastr.error("ERROR", get(response, "errors", "Quickbooks Error."));
        });
    },
    [locationId]
  );

  const inventoryTrackingEnabled =
    get(settings, QUICKBOOKS_INVENTORY_TRACKING) === ENABLED;

  const clearFields = () =>
    [
      REDUCE_COST_AT_SALE,
      REDUCE_REPAIR_COST_AT_SALE,
      SOLD_INVENTORY_ITEM,
      INVENTORY_ACCOUNT
    ].map(f => updateSetting(f, null));

  return (
    <Modal open={editing} close={close} dialogClass="modal-md">
      <Header>
        <Close close={close} />
        <h4 className="modal-title">Quickbooks Settings</h4>
      </Header>
      <Body>
        <FormRow>
          <h4>Settings</h4>
        </FormRow>
        <FormRow>
          <Col md={6}>
            <CheckGroup title="Default Quickbooks Account">
              <Check
                label="Default Quickbooks Account"
                onChange={val => setDefaultQuickbooksAccount(val)}
                input={{
                  name: "default_quickbooks_account",
                  value: defaultQuickbooksAccount,
                  onChange: () => {}
                }}
              />
            </CheckGroup>
          </Col>
          <Col md={4}>
            <label className="control-label" htmlFor="Start Sync Date">
              Start Sync Date
            </label>
            <input
              className="form-control"
              type="date"
              value={get(settings, "sync_date")}
              onChange={e => updateSetting("sync_date", e.target.value)}
            />
          </Col>
        </FormRow>
        <br />
        <FormRow>
          <label className="control-label" htmlFor="Select Type">
            Quickbooks Inventory Tracking&nbsp;<NoteTooltip
              note="Quickbooks enables automatic tracking of inventory.  Infotrader replaces quickbooks inventory tracking and can automatically enter journal entries.  However if you have inventory tracking enabled, Infotrader will rely on quickbooks inventory adjustments and will notify of differences.  Please review our quickbooks integration guide for further details."
              icon="fa-info-circle"
              color="#545454"
            />
          </label>
        </FormRow>
        <FormRow>
          <Col md={6}>
            <CheckGroup title="Use Infotrader Inventory Tracking">
              <Check
                label="Use Infotrader"
                onChange={val =>
                  updateSetting(
                    QUICKBOOKS_INVENTORY_TRACKING,
                    val ? DISABLED : ENABLED
                  )}
                input={{
                  name: QUICKBOOKS_INVENTORY_TRACKING,
                  value:
                    get(settings, QUICKBOOKS_INVENTORY_TRACKING) === DISABLED,
                  onChange: () => {
                    clearFields();
                  }
                }}
              />
            </CheckGroup>
          </Col>
          <Col md={6}>
            <CheckGroup title="Track Inventory In Quickbooks">
              <Check
                label="Track Inventory"
                onChange={val =>
                  updateSetting(
                    QUICKBOOKS_INVENTORY_TRACKING,
                    val ? ENABLED : DISABLED
                  )}
                input={{
                  name: QUICKBOOKS_INVENTORY_TRACKING,
                  value:
                    get(settings, QUICKBOOKS_INVENTORY_TRACKING) === ENABLED,
                  onChange: () => {
                    clearFields();
                  }
                }}
              />
            </CheckGroup>
          </Col>
        </FormRow>
        {!inventoryTrackingEnabled && (
          <FormRow>
            <Col md={6}>
              <CheckGroup title={properCase(REDUCE_COST_AT_SALE)}>
                <Check
                  label={properCase(REDUCE_COST_AT_SALE)}
                  onChange={val => updateSetting(REDUCE_COST_AT_SALE, val)}
                  input={{
                    name: REDUCE_COST_AT_SALE,
                    value: get(settings, REDUCE_COST_AT_SALE),
                    onChange: () => {}
                  }}
                />
              </CheckGroup>
            </Col>
          </FormRow>
        )}
        {inventoryTrackingEnabled && (
          <FormRow>
            <Col md={6}>
              <CheckGroup title={properCase(REDUCE_REPAIR_COST_AT_SALE)}>
                <Check
                  label={properCase(REDUCE_REPAIR_COST_AT_SALE)}
                  onChange={val =>
                    updateSetting(REDUCE_REPAIR_COST_AT_SALE, val)}
                  input={{
                    name: REDUCE_REPAIR_COST_AT_SALE,
                    value: get(settings, REDUCE_REPAIR_COST_AT_SALE),
                    onChange: () => {}
                  }}
                />
              </CheckGroup>
            </Col>
          </FormRow>
        )}
        {prefs.length > 0 && [
          <br />,
          <hr className="hr-xs hr-dashed" />,
          <FormRow>
            <h4>Sales Orders</h4>
          </FormRow>,
          <FormRow>
            <label className="control-label" htmlFor="Customer Ref #">
              Customer Ref #
            </label>
            <select
              className="form-control"
              id="applicable-type"
              value={get(settings, QUICKBOOKS_CUSTOMER_PURCHASE_FIELD, null)}
              onChange={ev => {
                updateSetting(
                  QUICKBOOKS_CUSTOMER_PURCHASE_FIELD,
                  ev.target.value
                );
              }}
            >
              <option value="">(Select one)</option>
              {prefs.map(f => <option value={f.name}>{f.string_value}</option>)}
            </select>
          </FormRow>
        ]}
        <FormRow>
          <label className="control-label" htmlFor="Inventory Account">
            Inventory Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "inventory_account.name", null)}
              onSelect={val => updateSetting("inventory_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Income Account">
            Income Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "income_account.name", null)}
              onSelect={val => updateSetting("income_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="COGS Account">
            Cost of Goods Sold Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "cogs_account.name", null)}
              onSelect={val => updateSetting("cogs_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Restocking Fee">
            Sold Inventory Item
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "sold_inventory_item.name", null)}
              onSelect={val => updateSetting(SOLD_INVENTORY_ITEM, val)}
              search={searchItems(NON_INVENTORY)}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Accounts"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="exchange_sales_item">
            Exchange Sales Item
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "exchange_sales_item.name", null)}
              onSelect={val => updateSetting("exchange_sales_item", val)}
              search={searchItems([NON_INVENTORY, SERVICE].join(","))}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Accounts"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Hazmat Fee">
            Core Repair Charge
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "core_repair_charge.name", null)}
              onSelect={val => updateSetting("core_repair_charge", val)}
              search={searchItems(SERVICE)}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Items"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Hazmat Fee">
            Hazmat Fee
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "hazmat_fee_item.name", null)}
              onSelect={val => updateSetting("hazmat_fee_item", val)}
              search={searchItems([NON_INVENTORY, SERVICE].join(","))}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Items"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Hazmat Fee">
            Container Fee
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "container_fee_item.name", null)}
              onSelect={val => updateSetting("container_fee_item", val)}
              search={searchItems([NON_INVENTORY, SERVICE].join(","))}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Items"
            />
          </div>
        </FormRow>

        <br />
        <hr className="hr-xs hr-dashed" />
        <FormRow>
          <h4>Purchase Orders</h4>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="purchase_orders">
            Quickbooks Type
          </label>
          <select
            className="form-control"
            name={PURCHASE_ORDERS_QUICKBOOKS_TYPE}
            onChange={e =>
              updateSetting(PURCHASE_ORDERS_QUICKBOOKS_TYPE, e.target.value)}
            value={get(settings, PURCHASE_ORDERS_QUICKBOOKS_TYPE)}
          >
            <option value="">Select Default</option>
            {PURCHASE_ORDER_QUICKBOOKS_TYPES.map(r => (
              <option key={r.value} value={r.value}>
                {r.text}
              </option>
            ))}
          </select>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="A/P Account">
            Accounts Payable Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "accounts_payable_account.name", null)}
              onSelect={val => updateSetting("accounts_payable_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="A/P Account">
            Purchase Exchange Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "purchase_exchange_account.name", null)}
              onSelect={val => updateSetting("purchase_exchange_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <br />
        <hr className="hr-xs hr-dashed" />
        <FormRow>
          <h4>Repair Orders</h4>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Repair Account">
            Repairs
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "repair_account.name", null)}
              onSelect={val => updateSetting("repair_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
        <br />
        <hr className="hr-xs hr-dashed" />
        <FormRow>
          <h4>Managed Repair Orders</h4>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Restocking Fee">
            Managed Repair Item
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "managed_repair_item.name", null)}
              onSelect={val => updateSetting("managed_repair_item", val)}
              search={searchItems([NON_INVENTORY, SERVICE].join(","))}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Items"
            />
          </div>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="Repair Account">
            Managed Repair Expense Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "managed_repair_account.name", null)}
              onSelect={val => updateSetting("managed_repair_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>

        <br />
        <hr className="hr-xs hr-dashed" />
        <FormRow>
          <h4>Other</h4>
        </FormRow>
        <FormRow>
          <label className="control-label" htmlFor="A/R Account">
            A/R Account
          </label>
          <div className="input-group">
            <AutoComplete
              format={result => result.name}
              value={get(settings, "ar_account.name", null)}
              onSelect={val => updateSetting("ar_account", val)}
              search={search}
              renderItem={(item, isHighlighted) => (
                <li key={item.id} className={isHighlighted ? "selected" : ""}>
                  <div>
                    <strong>{item.fully_qualified_name}</strong>
                  </div>
                </li>
              )}
              placeholder="Search Account"
            />
          </div>
        </FormRow>
      </Body>
      <Footer>
        <AppForm.Cancel close={close} editable />
        <AppForm.Submit
          disabled={false}
          editable
          submitting={submitting}
          onClick={() => {
            saveSetting();
            close();
          }}
        />
      </Footer>
    </Modal>
  );
}

export default function AccountSelectionModal({ location }) {
  const [settings, setSetting] = useState(null);
  const [defaultQuickbooksAccount, setDefaultQuickbooksAccount] = useState(
    false
  );
  const [closed, toggle] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changes, setChanges] = useState(0);

  if (!location.quickbook_realm_id) return null;
  if (!location.quickbook_access_token) return null;
  useEffect(() => {
    api.locations.locationSetting.show(location.id).then(({ data }) => {
      setSetting(get(data, "settings.quickbooks", {}));
      setDefaultQuickbooksAccount(data.default_quickbooks_account);

      return function cleanup() {};
    });
  }, []);

  const saveSetting = useCallback(() => {
    setSubmitting(true);
    api.locations.locationSetting
      .update(location.id, { quickbooks: settings }, defaultQuickbooksAccount)
      .then(({ data: resp }) => {
        setSubmitting(false);
        setSetting(get(resp, "settings.quickbooks"));
      });
  });

  const updateSetting = useCallback(
    (path, value) => {
      setSetting(set(settings, path, value));
      setChanges(changes + 1);
    },
    [settings, changes]
  );

  if (!settings) return null;

  return (
    <React.Fragment>
      <button
        style={{ cursor: "pointer", marginTop: "1rem" }}
        onClick={() => toggle(!closed)}
        className="btn btn-default"
      >
        Location Settings
      </button>
      <SettingsModal
        editing={!closed}
        close={() => toggle(true)}
        defaultQuickbooksAccount={defaultQuickbooksAccount}
        locationId={location.id}
        settings={settings}
        setDefaultQuickbooksAccount={setDefaultQuickbooksAccount}
        saveSetting={saveSetting}
        submitting={submitting}
        updateSetting={updateSetting}
        changes={changes}
      />
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// ./app/modules/Admin/Integrations/AccountSelectionModal.jsx